import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { NAV_LINKS } from "@/constants/site";
import { Logo } from "@/components/ui/Logo";
import { MobileNavSheet } from "@/components/layout/MobileNavSheet";

export const StickyNavbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!onLanding) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    NAV_LINKS.forEach((l) => {
      const el = document.getElementById(l.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [onLanding]);

  const goto = (id) => {
    setOpen(false);
    if (onLanding) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        className="sticky top-0 z-40 w-full"
        style={{
          background: scrolled ? "rgba(7,11,29,0.86)" : "rgba(7,11,29,0.55)",
          backdropFilter: "blur(14px)",
          borderBottom: `1px solid rgba(255,255,255,${scrolled ? 0.12 : 0.06})`,
          transition: "background-color 220ms ease, border-color 220ms ease",
        }}
      >
        <div className="gm-container flex h-16 items-center justify-between gap-4">
          <Logo size={36} />

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main navigation">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => goto(link.id)}
                data-testid={`nav-link-${link.id}`}
                className="relative whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium hover:bg-white/5"
                style={{
                  color: active === link.id && onLanding ? "#fff" : "var(--gm-muted)",
                  transition: "color 180ms ease, background-color 180ms ease",
                }}
              >
                {link.label}
                {active === link.id && onLanding ? (
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-3 -bottom-0.5 h-[2px] rounded-full"
                    style={{ background: "var(--gm-red)" }}
                  />
                ) : null}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/free-access"
              data-testid="navbar-free-access-link"
              className="gm-btn-ghost hidden min-h-[44px] items-center whitespace-nowrap rounded-xl px-4 text-sm font-semibold sm:inline-flex"
            >
              Free access
            </Link>
            <button
              type="button"
              onClick={() => goto("pricing")}
              data-testid="navbar-primary-cta"
              className="gm-btn-red hidden min-h-[44px] items-center whitespace-nowrap rounded-xl px-4 text-sm font-bold sm:inline-flex"
            >
              Get Goldmap EA
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="mobile-nav-open-button"
              aria-label="Open menu"
              className="gm-btn-ghost inline-flex h-11 w-11 items-center justify-center rounded-xl lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <MobileNavSheet open={open} onOpenChange={setOpen} onNavigate={goto} />
    </>
  );
};
